<template>
  <div class="contact-page">
    <main class="container">
      <section class="contact-grid">
      
        <article class="card contact-card">
          <h2 class="title">Feedback</h2>
          <p class="lead">
            We value your thoughts and suggestions — share your feedback with us
            below.
          </p>

          <form
            @submit.prevent="submitContact"
            novalidate
            class="contact-form"
            :aria-busy="submitting"
          >
            <div class="row">
              <label for="name">Name</label>
              <input
                id="name"
                type="text"
                v-model="form.name"
                placeholder="Your name (optional)"
              />
            </div>

            <div class="row">
              <label for="email">Email <span class="req">*</span></label>
              <input
                id="email"
                type="email"
                v-model="form.email"
                placeholder="you@example.com"
                required
              />
              <div v-if="errors.email" class="field-error">
                {{ errors.email }}
              </div>
            </div>

            <div class="row">
              <label for="subject">Subject</label>
              <input
                id="subject"
                type="text"
                v-model="form.subject"
                placeholder="Short subject (optional)"
              />
            </div>

            <div class="row">
              <label for="message">Message <span class="req">*</span></label>
              <textarea
                id="message"
                v-model="form.message"
                rows="6"
                placeholder="Write your message here"
                required
              ></textarea>
              <div v-if="errors.message" class="field-error">
                {{ errors.message }}
              </div>
            </div>

            <div class="row consent-row">
              <label class="consent-label" for="consent">
                <input
                  id="consent"
                  type="checkbox"
                  v-model="form.consent"
                  required
                />
                <span>
                  I agree to the
                  <router-link to="/privacy-policy" target="_blank">Privacy Policy</router-link>
                  and consent to Ajonee storing my information to respond to this feedback.
                </span>
              </label>
              <div v-if="errors.consent" class="field-error">
                {{ errors.consent }}
              </div>
            </div>

            <div class="actions">
              <button
                class="cs-button-solid"
                type="submit"
                :disabled="submitting"
              >
                <span v-if="!submitting">Send Message</span>
                <span v-else>Sending…</span>
              </button>

              <button
                class="cs-button-solid"
                type="button"
                @click="resetForm"
                :disabled="submitting"
              >
                Reset
              </button>
            </div>

            <p v-if="success" class="success">
              Thanks — your message was sent. We'll reply soon.
            </p>

            <p v-if="serverError" class="field-error" role="alert">
              {{ serverError }}
            </p>
          </form>
        </article>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: "FeedbackForm",
  data() {
    return {
      // API endpoint used on submit — change if your backend path differs
      //apiEndpoint: "http://localhost:8080/api/email/feedback",

      form: {
        name: "",
        email: "",
        subject: "",
        message: "",
        consent: false,
      },
      errors: {},
      submitting: false,
      success: false,
      serverError: "",
    };
  },
  methods: {
    validate() {
      this.errors = {};
      if (!this.form.email) {
        this.errors.email = "Email is required.";
      } else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) {
        this.errors.email = "Enter a valid email address.";
      }
      if (!this.form.message || !this.form.message.trim()) {
        this.errors.message = "Please enter a message.";
      }
      if (!this.form.consent) {
        this.errors.consent = "Please agree to the Privacy Policy to continue.";
      }
      return Object.keys(this.errors).length === 0;
    },

    async submitContact() {
      this.serverError = "";

      if (!this.validate()) {
        const firstErr = Object.keys(this.errors)[0];
        if (firstErr) {
          const el = document.getElementById(
            firstErr === "email" ? "email" : "message"
          );
          if (el) el.focus();
        }
        return;
      }

      this.submitting = true;
      this.success = false;

      const nameToSend =
        this.form.name && this.form.name.trim()
          ? this.form.name.trim()
          : "Anonymous";

      const payload = {
        name: nameToSend,
        email: (this.form.email || "").trim(),
        subject: this.form.subject ? this.form.subject.trim() : "",
        message: this.form.message ? this.form.message.trim() : "",
        //source: "contact_form",
      };

      try {
        const res = await fetch("https://services.mviaso.com/api/email/feedback", {
        //const res = await fetch("http://localhost:8080/api/email/feedback", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            //"x-api-key": "02p9k04i8H0iLvZSDR0zVHCRgwjc8y4Q4BqfcM_s6sI"
          },
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          this.success = true;
          this.serverError = "";
          this.resetForm();

          setTimeout(() => {
            this.success = false;
          }, 5000);
        } else {
          let errText = `Server returned ${res.status}`;
          try {
            const data = await res.json();
            if (data && typeof data === "object") {
              if (data.errors) {
                this.errors = { ...this.errors, ...data.errors };
              }
              if (data.message) errText = data.message;
              else if (data.error) errText = data.error;
            }
          } catch {
            errText = res.statusText || errText;
          }
          this.serverError = `Failed to send message: ${errText}`;
        }
      } catch (err) {
        console.error("submitContact error:", err);
        this.serverError = "Network error — please try again later.";
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.form.name = "";
      this.form.email = "";
      this.form.subject = "";
      this.form.message = "";
      this.form.consent = false;
      this.errors = {};
      this.serverError = "";
    },
  },
};
</script>
<style scoped>
:root {
  --primary: #0D3B66;
  --headerColor: #0D2438;
  --sectionPadding: 4rem 1rem;
}

/* ==============================
   Feedback Form - Modern Design
   ============================== */

.contact-page {
  padding: var(--sectionPadding);
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100vh;
}

.container {
  width: 100%;
  max-width: 42rem;
  margin: 0 auto;
}

.contact-card {
  background-color: #ffffff;
  border: 1px solid var(--primary);
  border-radius: 1rem;
  padding: 2.5rem 2rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.title {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  color: var(--headerColor);
  margin-bottom: 0.25rem;
  text-align: center;
}

.lead {
  color: #666;
  text-align: center;
  max-width: 32rem;
  margin-bottom: 1rem;
  line-height: 1.5;
}

/* ===== Form Styles ===== */
.contact-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  font-size: 1rem;
  font-weight: 500;
  color: var(--headerColor);
}

.req {
  color: #e53935;
  margin-left: 2px;
}

.consent-row {
  gap: 0.25rem;
}
.consent-label {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--bodyTextColor);
}
.consent-label input[type="checkbox"] {
  margin-top: 0.2rem;
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
  accent-color: var(--primary);
}
.consent-label a {
  color: var(--primary);
  font-weight: 700;
}

input,
textarea {
  font-size: 1rem;
  width: 100%;
  background-color: #f7f7f7;
  border: 1px solid var(--primary);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  box-sizing: border-box;
  color: var(--headerColor);
  transition: all 0.3s ease;
}

input:focus,
textarea:focus {
  outline: none;
  background-color: #fff;
  border-color: #005fa3;
  box-shadow: 0 0 0 3px rgba(0, 119, 204, 0.15);
}

textarea {
  min-height: 7rem;
  resize: vertical;
}

/* ===== Buttons ===== */
.actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.cs-button-solid {
  font-size: 1rem;
  line-height: 3.5rem;
  text-decoration: none;
  font-weight: 700;
  overflow: hidden;
  margin: 0;
  color: #fff;
  padding: 0 3rem;
  border-radius: 1.875rem;
  background-color: var(--primary);
  display: inline-block;
  position: relative;
  z-index: 1;
  transition: color 0.3s;
  border: none;
}

.cs-button-solid:before {
  content: "";
  position: absolute;
  display: block;
  height: 100%;
  width: 0%;
  background: #1a1a1a;
  opacity: 1;
  top: 0;
  left: 0;
  z-index: -1;
  transition: width 0.3s;
}

.cs-button-solid:hover:before {
  width: 100%;
}



/* ===== Messages & Errors ===== */
.field-error {
  color: #e53935;
  font-size: 0.875rem;
  margin-top: -0.25rem;
}

.success {
  color: #2e7d32;
  font-size: 0.95rem;
  text-align: center;
  margin-top: 1rem;
  font-weight: 500;
}

/* ===== Responsive ===== */
@media (min-width: 768px) {
  .contact-card {
    padding: 3rem;
  }
}

@media (min-width: 1024px) {
  .contact-card {
    max-width: 44rem;
  }
}
</style>
